/**
 * Factory Dashboard WebSocket Hook
 *
 * React hook for real-time updates in AI Factory Dashboard (Story 74)
 *
 * Usage:
 * ```tsx
 * const { metrics, isConnected, error } = useFactoryWebSocket(siteId, { autoConnect: true });
 * ```
 *
 * NOTE: autoConnect is disabled by default to prevent connection storms.
 * Enable it explicitly only when real-time updates are needed.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { SOCKET_URL } from '@/config/api';

// Event types
export interface PipelineProgressEvent {
  stage: string;
  count: number;
  percentage: number;
  timestamp: string;
}

export interface ProductCompletedEvent {
  productId: string;
  productName: string;
  status: 'completed' | 'failed';
  stage: string;
  timestamp: string;
}

export interface QualityAlertEvent {
  severity: 'warning' | 'critical';
  type: string;
  message: string;
  affectedProducts: number;
  timestamp: string;
}

export interface BatchCompletedEvent {
  batchId: string;
  state: 'pilot' | 'full_run';
  totalProducts: number;
  completedProducts: number;
  failedProducts: number;
  timestamp: string;
}

export interface CurrentBatch {
  id: string;
  state: string;
  totalProducts: number;
  completedProducts: number;
  progress: number;
  startedAt: string;
}

export interface TodayStats {
  productsEnriched: number;
  pagesValidated: number;
  specsResearched: number;
}

export interface PipelineStage {
  stage: string;
  count: number;
}

export interface QualityStats {
  passed: number;
  flagged: number;
  blocked: number;
  total: number;
  passRate: number;
}

export interface FactoryMetrics {
  currentBatch: CurrentBatch | null;
  today: TodayStats;
  pipeline: PipelineStage[];
  quality: QualityStats;
  isInitial?: boolean;
  timestamp: string;
}

interface UseFactoryWebSocketOptions {
  onPipelineProgress?: (event: PipelineProgressEvent) => void;
  onProductCompleted?: (event: ProductCompletedEvent) => void;
  onQualityAlert?: (event: QualityAlertEvent) => void;
  onBatchCompleted?: (event: BatchCompletedEvent) => void;
  autoConnect?: boolean;
}

interface UseFactoryWebSocketReturn {
  metrics: FactoryMetrics | null;
  isConnected: boolean;
  error: string | null;
  lastUpdate: Date | null;
  connect: () => void;
  disconnect: () => void;
  requestRefresh: () => void;
}

// Get the WebSocket URL
const getSocketUrl = () => {
  return SOCKET_URL || 'http://localhost:3001';
};

// Minimum time between connection attempts (ms)
const CONNECTION_THROTTLE_MS = 5000;

export function useFactoryWebSocket(
  siteId: string | null,
  options: UseFactoryWebSocketOptions = {}
): UseFactoryWebSocketReturn {
  const {
    onPipelineProgress,
    onProductCompleted,
    onQualityAlert,
    onBatchCompleted,
    autoConnect = false // DISABLED by default to prevent connection storms
  } = options;

  const [metrics, setMetrics] = useState<FactoryMetrics | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // Refs for stable references
  const socketRef = useRef<Socket | null>(null);
  const lastConnectionAttemptRef = useRef<number>(0);
  const connectionIdRef = useRef<number>(0); // Track connection instance

  // Refs for callbacks to avoid reconnect cycles
  const onPipelineProgressRef = useRef(onPipelineProgress);
  const onProductCompletedRef = useRef(onProductCompleted);
  const onQualityAlertRef = useRef(onQualityAlert);
  const onBatchCompletedRef = useRef(onBatchCompleted);

  // Keep callback refs updated synchronously
  onPipelineProgressRef.current = onPipelineProgress;
  onProductCompletedRef.current = onProductCompleted;
  onQualityAlertRef.current = onQualityAlert;
  onBatchCompletedRef.current = onBatchCompleted;

  // Create socket connection
  const createConnection = useCallback((targetSiteId: string, connectionId: number) => {
    // Throttle connection attempts
    const now = Date.now();
    const timeSinceLastAttempt = now - lastConnectionAttemptRef.current;
    if (timeSinceLastAttempt < CONNECTION_THROTTLE_MS) {
      console.log('[Factory WebSocket] Throttled - too many connection attempts');
      return null;
    }
    lastConnectionAttemptRef.current = now;

    const socketUrl = getSocketUrl();
    console.log('[Factory WebSocket] Creating connection to:', socketUrl, 'for site:', targetSiteId);

    const socket = io(socketUrl, {
      transports: ['polling', 'websocket'],
      withCredentials: true,
      query: { type: 'factory-dashboard' },
      reconnectionAttempts: 3,
      reconnectionDelay: 3000,
      timeout: 10000,
      autoConnect: true
    });

    socket.on('connect', () => {
      // Check if this connection is still valid
      if (connectionId !== connectionIdRef.current) {
        console.log('[Factory WebSocket] Stale connection detected, disconnecting');
        socket.disconnect();
        return;
      }
      console.log('[Factory WebSocket] Connected, joining room for site:', targetSiteId);
      setIsConnected(true);
      setError(null);
      socket.emit('join-factory', { siteId: targetSiteId });
    });

    socket.on('factory:connected', (data) => {
      console.log('[Factory WebSocket] Joined factory room:', data);
    });

    socket.on('factory:metrics_update', (data: FactoryMetrics) => {
      if (connectionId !== connectionIdRef.current) return;
      console.log('[Factory WebSocket] Metrics update received');
      setMetrics(data);
      setLastUpdate(new Date());
    });

    socket.on('factory:pipeline_progress', (data: PipelineProgressEvent) => {
      if (connectionId !== connectionIdRef.current) return;
      console.log('[Factory WebSocket] Pipeline progress:', data);
      onPipelineProgressRef.current?.(data);
    });

    socket.on('factory:product_completed', (data: ProductCompletedEvent) => {
      if (connectionId !== connectionIdRef.current) return;
      console.log('[Factory WebSocket] Product completed:', data);
      onProductCompletedRef.current?.(data);
    });

    socket.on('factory:quality_alert', (data: QualityAlertEvent) => {
      if (connectionId !== connectionIdRef.current) return;
      console.log('[Factory WebSocket] Quality alert:', data);
      onQualityAlertRef.current?.(data);
    });

    socket.on('factory:batch_completed', (data: BatchCompletedEvent) => {
      if (connectionId !== connectionIdRef.current) return;
      console.log('[Factory WebSocket] Batch completed:', data);
      onBatchCompletedRef.current?.(data);
    });

    socket.on('factory:error', (data: { message: string }) => {
      if (connectionId !== connectionIdRef.current) return;
      console.error('[Factory WebSocket] Error:', data.message);
      setError(data.message);
    });

    socket.on('disconnect', (reason) => {
      console.log('[Factory WebSocket] Disconnected:', reason);
      if (connectionId === connectionIdRef.current) {
        setIsConnected(false);
      }
    });

    socket.on('connect_error', (err) => {
      if (connectionId !== connectionIdRef.current) return;
      console.error('[Factory WebSocket] Connection error:', err.message);
      setError(`Connection error: ${err.message}`);
      setIsConnected(false);
    });

    return socket;
  }, []);

  // Manual connect function
  const connect = useCallback(() => {
    if (!siteId) {
      setError('No site ID provided');
      return;
    }

    // Disconnect existing if any
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    // Increment connection ID to invalidate any pending old connections
    connectionIdRef.current++;
    const socket = createConnection(siteId, connectionIdRef.current);
    if (socket) {
      socketRef.current = socket;
    }
  }, [siteId, createConnection]);

  // Manual disconnect function
  const disconnect = useCallback(() => {
    // Increment connection ID to invalidate
    connectionIdRef.current++;

    if (socketRef.current) {
      console.log('[Factory WebSocket] Manual disconnect');
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    }
  }, []);

  // Request metrics refresh
  const requestRefresh = useCallback(() => {
    if (socketRef.current?.connected && siteId) {
      socketRef.current.emit('factory:request-metrics', { siteId });
    }
  }, [siteId]);

  // Auto-connect effect - only runs when siteId or autoConnect changes
  useEffect(() => {
    // Skip if no siteId or autoConnect is disabled
    if (!siteId || !autoConnect) {
      return;
    }

    // Increment connection ID
    const thisConnectionId = ++connectionIdRef.current;

    const socket = createConnection(siteId, thisConnectionId);
    if (socket) {
      socketRef.current = socket;
    }

    // Cleanup function
    return () => {
      console.log('[Factory WebSocket] Effect cleanup');
      // Only disconnect if this is still the active connection
      if (thisConnectionId === connectionIdRef.current && socketRef.current) {
        socketRef.current.emit('leave-factory', { siteId });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [siteId, autoConnect, createConnection]);

  return {
    metrics,
    isConnected,
    error,
    lastUpdate,
    connect,
    disconnect,
    requestRefresh
  };
}

export default useFactoryWebSocket;
